import { X, AlertCircle, ExternalLink, RefreshCw } from 'lucide-react';
import { marked } from 'marked';
import { useTranslation } from 'react-i18next';
import type { AISummary } from '../lib/types';

interface SummaryModalProps {
  summary: AISummary | null;
  isLoading: boolean;
  error?: string | null;
  paymentUrl?: string | null;
  onClose: () => void;
  onRefresh?: () => void;
}

export function SummaryModal({ 
  summary, 
  isLoading, 
  error, 
  paymentUrl, 
  onClose,
  onRefresh
}: SummaryModalProps) {
  const { t } = useTranslation();

  // Only render if we have something to show
  if (!summary && !isLoading && !error && !paymentUrl) return null;

  const handleModalClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  // Configure marked options
  marked.setOptions({
    gfm: true, // GitHub Flavored Markdown
    breaks: true, // Convert line breaks to <br>
    headerIds: true, // Add IDs to headers
    mangle: false, // Don't escape HTML
    sanitize: false // Don't sanitize HTML (we trust OpenAI's output)
  });

  // Parse markdown content
  const getHtmlContent = (markdown: string) => {
    try {
      return marked.parse(markdown);
    } catch (error) {
      console.error('Error parsing markdown:', error);
      return markdown; // Fallback to raw content
    }
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 overflow-hidden"
      onClick={onClose}
    >
      <div 
        className="w-full max-w-2xl rounded-lg bg-white shadow-xl flex flex-col max-h-[90vh]"
        onClick={handleModalClick}
      >
        <div className="flex items-center justify-between border-b p-4 flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-900">{t('summary.title')}</h2>
          <button
            onClick={onClose}
            className="rounded-lg p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <div className="overflow-y-auto flex-1 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
          <div className="p-6">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-center">
                  <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-primary-500 border-r-transparent"></div>
                  <p className="mt-4 text-gray-600">{t('summary.loading')}</p>
                </div>
              </div>
            ) : error ? (
              <div className="flex items-start gap-3 rounded-lg bg-red-50 p-4 text-red-700">
                <AlertCircle className="h-5 w-5 flex-shrink-0" />
                <div>
                  <p className="font-medium">{t('summary.error.title')}</p>
                  <p className="mt-1 text-sm text-red-600">{error}</p>
                  <button
                    onClick={onClose}
                    className="mt-3 text-sm font-medium text-red-700 hover:text-red-800"
                  >
                    {t('summary.error.retry')}
                  </button>
                </div>
              </div>
            ) : paymentUrl ? (
              <div className="text-center py-6">
                <p className="mb-6 text-gray-700">
                  {t('summary.payment.description')}
                </p>
                <a
                  href={paymentUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative inline-flex items-center justify-center overflow-hidden rounded-lg bg-gradient-to-br from-accent-500 to-primary-500 p-[2px] transition-all hover:from-accent-600 hover:to-primary-600"
                >
                  <div className="absolute inset-0 bg-[length:200%_100%] bg-gradient-to-r from-accent-500/0 via-white/25 to-accent-500/0 group-hover:animate-shimmer" />
                  <div className="relative flex items-center gap-2 rounded-[6px] bg-gradient-to-br from-accent-500 to-primary-500 px-6 py-3">
                    <ExternalLink className="h-5 w-5 text-white" />
                    <span className="font-medium text-white">{t('summary.payment.button')}</span>
                  </div>
                </a>
              </div>
            ) : summary ? (
              <div className="prose prose-gray max-w-none">
                <div 
                  className="markdown-content space-y-6"
                  dangerouslySetInnerHTML={{ __html: getHtmlContent(summary.content) }}
                />
              </div>
            ) : null}
          </div>
        </div>

        <div className="border-t bg-gray-50 p-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-500">
              {t('summary.disclaimer')}
            </p>
            {onRefresh && summary?.payment_status === 'completed' && (
              <button
                onClick={() => {
                  onClose();
                  onRefresh();
                }}
                className="ml-4 flex items-center gap-2 rounded-lg bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 border border-gray-200 transition-colors"
              >
                <RefreshCw className="h-4 w-4" />
                <span>{t('summary.refresh')}</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}